import { useState } from "react";
import { Calculator, CheckCircle } from "lucide-react";
import {
  calculatePostSaleTax,
  applyTaxToOS,
  type PostSaleTaxProjection,
} from "../../api/client";
import { fmtCurrency } from "../../utils/format";
import { useToast } from "../../contexts/ToastContext";

interface PostSaleTaxTabProps {
  dealId: string;
  purchasePrice?: number;
  totalLeasableArea?: number;
  onTaxApplied?: () => void;
}

export default function PostSaleTaxTab({
  dealId,
  purchasePrice: initialPurchasePrice,
  totalLeasableArea,
  onTaxApplied,
}: PostSaleTaxTabProps) {
  const toast = useToast();

  const [purchasePrice, setPurchasePrice] = useState<string>(
    initialPurchasePrice ? initialPurchasePrice.toString() : "",
  );
  const [millageRate, setMillageRate] = useState<string>("22.1847");
  const [assessmentRatio, setAssessmentRatio] = useState<string>("85");
  const [assessmentCap, setAssessmentCap] = useState<string>("10");
  const [holdPeriod, setHoldPeriod] = useState<string>("10");
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [projection, setProjection] = useState<PostSaleTaxProjection | null>(
    null,
  );

  async function handleCalculate() {
    const pp = parseFloat(purchasePrice);
    const mr = parseFloat(millageRate);
    const ar = parseFloat(assessmentRatio) / 100;
    const cap = parseFloat(assessmentCap);
    const hp = parseInt(holdPeriod);

    if (isNaN(pp) || pp <= 0) {
      toast.error("Enter a valid purchase price.");
      return;
    }
    if (isNaN(mr) || mr <= 0) {
      toast.error("Enter a valid millage rate.");
      return;
    }

    setLoading(true);
    try {
      const result = await calculatePostSaleTax(
        dealId,
        pp,
        mr / 1000, // convert mills to ratio
        isNaN(ar) ? 0.85 : ar,
        isNaN(cap) ? null : cap / 100,
        isNaN(hp) || hp < 1 ? 10 : hp,
      );
      setProjection(result);
    } catch (e: any) {
      toast.error(e?.message || "Calculation failed");
    } finally {
      setLoading(false);
    }
  }

  async function handleApply() {
    if (!projection) return;
    setApplying(true);
    try {
      await applyTaxToOS(
        dealId,
        "post_sale",
        projection.year1_estimated_tax,
      );
      toast.success(
        `Applied ${fmtCurrency(projection.year1_estimated_tax)} to Operating Statement`,
      );
      onTaxApplied?.();
    } catch (e: any) {
      toast.error(e?.message || "Failed to apply tax");
    } finally {
      setApplying(false);
    }
  }

  function taxPsf(tax: number): string {
    if (!totalLeasableArea || totalLeasableArea <= 0) return "--";
    return `$${(tax / totalLeasableArea).toFixed(2)}/SF`;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h3 className="font-display text-lg font-semibold text-bark-800">
          Post-Sale Tax Assumptions
        </h3>
        <p className="text-sm text-bark-400 mt-1">
          Model your expected property taxes after acquisition based on purchase
          price, millage rate, and Florida assessment rules.
        </p>
      </div>

      {/* Input form */}
      <div className="bg-white rounded-xl border border-grove-300 p-6 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="text-sm font-medium text-bark-700 block mb-1.5">
              Purchase Price
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-bark-400 text-sm">
                $
              </span>
              <input
                type="text"
                value={purchasePrice ? Number(purchasePrice).toLocaleString("en-US") : ""}
                onChange={(e) => setPurchasePrice(e.target.value.replace(/[^0-9.]/g, ""))}
                placeholder="6,000,000"
                className="w-full pl-7 pr-3 py-2 border border-grove-300 rounded-lg text-sm text-bark-700 placeholder:text-bark-300 focus:outline-none focus:ring-2 focus:ring-forest-500"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-bark-700 block mb-1.5">
              Millage Rate (mills)
            </label>
            <input
              type="number"
              step="0.0001"
              value={millageRate}
              onChange={(e) => setMillageRate(e.target.value)}
              className="w-full px-3 py-2 border border-grove-300 rounded-lg text-sm text-bark-700 focus:outline-none focus:ring-2 focus:ring-forest-500"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-bark-700 block mb-1.5">
              Assessment Ratio (%)
            </label>
            <input
              type="number"
              step="1"
              min="0"
              max="100"
              value={assessmentRatio}
              onChange={(e) => setAssessmentRatio(e.target.value)}
              className="w-full px-3 py-2 border border-grove-300 rounded-lg text-sm text-bark-700 focus:outline-none focus:ring-2 focus:ring-forest-500"
            />
            <p className="text-[11px] text-bark-400 mt-1">
              Typically 80-100% for commercial
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-bark-700 block mb-1.5">
              Annual Assessment Cap (%)
            </label>
            <input
              type="number"
              step="1"
              min="0"
              max="100"
              value={assessmentCap}
              onChange={(e) => setAssessmentCap(e.target.value)}
              className="w-full px-3 py-2 border border-grove-300 rounded-lg text-sm text-bark-700 focus:outline-none focus:ring-2 focus:ring-forest-500"
            />
            <p className="text-[11px] text-bark-400 mt-1">
              FL non-homestead commercial: 10% cap (Amendment 1, 2008)
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-bark-700 block mb-1.5">
              Hold Period (years)
            </label>
            <input
              type="number"
              step="1"
              min="1"
              max="30"
              value={holdPeriod}
              onChange={(e) => setHoldPeriod(e.target.value)}
              className="w-full px-3 py-2 border border-grove-300 rounded-lg text-sm text-bark-700 focus:outline-none focus:ring-2 focus:ring-forest-500"
            />
          </div>
        </div>

        <div className="mt-5">
          <button
            onClick={handleCalculate}
            disabled={loading}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-forest-700 text-white text-sm font-semibold rounded-lg hover:bg-forest-600 transition-all disabled:opacity-50"
          >
            <Calculator className="w-4 h-4" />
            {loading ? "Calculating..." : "Calculate"}
          </button>
        </div>
      </div>

      {/* Results */}
      {projection && (
        <>
          {/* Year 1 summary */}
          <div className="bg-honey-50 border border-honey-200 rounded-xl p-6">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-honey-700 mb-3">
              Year 1 Estimated Tax
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-bark-500">Assessed Value</span>
                <div className="text-lg font-bold text-bark-800 tabular-nums">
                  {fmtCurrency(projection.year1_assessed_value)}
                </div>
                <span className="text-xs text-bark-400">
                  = {fmtCurrency(projection.purchase_price)} x{" "}
                  {(projection.assessment_ratio * 100).toFixed(0)}%
                </span>
              </div>
              <div>
                <span className="text-sm text-bark-500">Estimated Tax</span>
                <div className="text-lg font-bold text-forest-700 tabular-nums">
                  {fmtCurrency(projection.year1_estimated_tax)} /yr
                </div>
                <span className="text-xs text-bark-400">
                  = {fmtCurrency(projection.year1_assessed_value)} x{" "}
                  {(projection.millage_rate * 1000).toFixed(4)} mills
                </span>
              </div>
            </div>
          </div>

          {/* Projection table */}
          <div className="bg-white rounded-xl border border-grove-300 shadow-sm overflow-x-auto">
            <table className="w-full data-table text-[13px]">
              <thead>
                <tr>
                  <th className="text-left">Year</th>
                  <th className="text-right">Assessed Value</th>
                  <th className="text-right">Annual Tax</th>
                  <th className="text-right">Tax PSF</th>
                </tr>
              </thead>
              <tbody>
                {projection.annual_projection.map((yr) => (
                  <tr
                    key={yr.year}
                    className={yr.year === 1 ? "bg-honey-50/50" : ""}
                  >
                    <td className="text-bark-600">{yr.year}</td>
                    <td className="text-right tabular-nums text-bark-700">
                      {fmtCurrency(yr.assessed_value)}
                    </td>
                    <td className="text-right tabular-nums font-medium text-bark-800">
                      {fmtCurrency(yr.tax)}
                    </td>
                    <td className="text-right tabular-nums text-bark-500">
                      {taxPsf(yr.tax)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Apply buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleApply}
              disabled={applying}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-honey-600 text-white text-sm font-semibold rounded-lg hover:bg-honey-500 transition-all disabled:opacity-50"
            >
              <CheckCircle className="w-4 h-4" />
              {applying
                ? "Applying..."
                : "Use Year 1 Tax in Operating Statement"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
